import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { listRows } from '@/api/supabaseClient';
import { usePublicRoutes } from '@/lib/publicRoutes';
import { heroExamSearchHref } from '@/lib/heroSlides';
import { HERO_FALLBACK_CHIPS, heroExamCategoryHref, popularExamCategories } from '@/lib/examCategories';

/**
 * Busca rápida de exames e chips de categorias da hero. Usado tanto no estado
 * carregado quanto no fallback estático, para que a conversão não dependa de
 * haver slides cadastrados no Supabase. Os chips refletem as categorias reais
 * dos exames ativos do catálogo e usam a lista fixa apenas como fallback.
 */
export default function HeroExamSearch() {
  const { path } = usePublicRoutes();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [chips, setChips] = useState(HERO_FALLBACK_CHIPS);

  useEffect(() => {
    let active = true;
    listRows('exames', { filters: { disponivel: true }, orderBy: 'created_at', ascending: false, limit: 100 })
      .then(data => {
        if (!active) return;
        const categories = popularExamCategories(data);
        if (categories.length) setChips(categories);
      })
      .catch(() => { /* mantém os chips fixos */ });
    return () => { active = false; };
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate(heroExamSearchHref(path('exams'), term));
  };

  return (
    <>
      <form
        role="search"
        onSubmit={handleSubmit}
        className="mx-auto mt-5 flex w-full max-w-md items-stretch border-2 border-white/80 bg-white shadow-[4px_4px_0_0_rgba(255,190,11,0.9)] focus-within:border-cdi-yellow"
      >
        <label htmlFor="hero-exam-search" className="sr-only">Buscar exame</label>
        <input
          id="hero-exam-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Busque seu exame..."
          autoComplete="off"
          className="h-12 w-full min-w-0 bg-transparent px-4 text-sm font-bold text-cdi-blue placeholder:text-cdi-carbon/40 focus:outline-none"
        />
        <button
          type="submit"
          aria-label="Buscar exame"
          className="flex w-14 shrink-0 items-center justify-center bg-cdi-yellow text-cdi-blue transition-colors hover:bg-cdi-blue hover:text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60"
        >
          <Search size={17} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </form>

      <div className="mt-4 hidden flex-wrap items-center justify-center gap-2 sm:flex">
        {chips.map(chip => (
          <Link
            key={chip}
            to={heroExamCategoryHref(path('exams'), chip)}
            className="border border-white/45 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-sm transition-colors hover:border-cdi-yellow hover:bg-cdi-yellow hover:text-cdi-blue"
          >
            {chip}
          </Link>
        ))}
      </div>
    </>
  );
}
